import { type HTMLAttributes, type PropsWithChildren, Ref, useEffect, useRef, useState } from "react"

import { Spinner, type SpinnerProps } from "~/shared/ui/kit/components/Spinner"
import type { ColorStyleProp } from "~/shared/ui/kit/types"


/**
 * Свойства `Loading`
 */
export type LoadingProps = {
	/** если true - будет отображаться спиннер, иначе - дочерние элементы */
	loading: boolean
	/**
	 * размер спиннера
	 * @defaultValue '20px'
	 */
	spinnerSize?: string
	/**
	 * подпись
	 * @defaultValue 'Загрузка...'
	 */
	title?: string
	/** ширина подписи */
	titleWidth?: string
	/**
	 * задержка отображения спиннера в секундах
	 * @defaultValue 0
	 */
	delay?: number
	spinnerProps?: SpinnerProps,
	ref?: Ref<HTMLDivElement>
} & HTMLAttributes<HTMLDivElement> & ColorStyleProp

/**
 * Обертка вокруг `Spinner` с подписью. Может содержать дочерние элементы, которые выведутся при `loading=false`.
 * Также поддерживает задержку отображения (`delay`), чтобы избежать отрисовки спиннера при быстрой загрузке данных.
 */
export function Loading({
							loading,
							spinnerSize = "1.25rem",
							title = "Загрузка...",
							titleWidth = undefined,
							delay = 0.25,
							children = undefined,
							spinnerProps = {},
							colorStyle = "secondary",
							...rootProps
						}: PropsWithChildren<LoadingProps>) {
	const timerId = useRef(0)
	const [show, setShow] = useState(delay === 0 ? true : false)

	useEffect(() => {
		if (loading)
			timerId.current = window.setTimeout(() => setShow(true), delay * 1000)
		else if (timerId.current) clearTimeout(timerId.current)

		return () => {
			if (timerId.current) clearTimeout(timerId.current)
		}
	}, [delay, loading])

	if (!loading) return children ? <>{children}</> : null

	if (!show) return null

	return (
		<div className="flex justify-center" {...rootProps}>
			<div className="flex items-center w-[100%]">
				<Spinner colorStyle={colorStyle} scale={1} style={{ width: spinnerSize }} {...spinnerProps} />
				<div
					style={{
						textAlign: "center",
						width: titleWidth,
						marginLeft: "0.5rem"
					}}
				>
					{title}
				</div>
			</div>
		</div>
	)
}
